import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Layers, Palette, ShieldCheck, Building2, Smartphone, ExternalLink } from "lucide-react";
import { THEME_LIST } from "@/lib/themes";

const features = [
  { icon: Layers, title: "Multi-tenant from day one", desc: "Each business runs an isolated app under their slug, branding & catalog." },
  { icon: Palette, title: "Six curated themes", desc: "Tenants pick a polished aesthetic — colors, fonts, radii — no design skills needed." },
  { icon: Smartphone, title: "Installable PWA", desc: "Customers add the tenant's app to their home screen. Lightning fast." },
  { icon: ShieldCheck, title: "Role-based & isolated", desc: "Super Admin, Tenant Admin, Seller, Customer — strict data boundaries." },
];

const stats = [
  { label: "Portals", value: "04" },
  { label: "Theme presets", value: "06" },
  { label: "Booking statuses", value: "08" },
  { label: "Subscription tiers", value: "05" },
];

export default function MarketingHome() {
  return (
    <div className="marketing min-h-screen" data-testid="marketing-home">
      {/* Top bar */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#0A0A0A]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 px-4 sm:px-6 py-4">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-9 w-9 rounded-lg bg-[#EE6A4D] flex items-center justify-center font-bold text-black flex-shrink-0">L</div>
            <div className="leading-none min-w-0">
              <div className="font-bold tracking-tight text-lg truncate">LocalAppStore</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 truncate">Services Platform</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-7 text-sm text-white/70">
            <a href="#features" className="hover:text-white transition">Features</a>
            <a href="#themes" className="hover:text-white transition">Themes</a>
            <a href="#demo" className="hover:text-white transition">Live demo</a>
            <Link to="/super/login" className="hover:text-white transition" data-testid="nav-super-login">Super Admin</Link>
          </nav>
          <Link to="/t/services" className="inline-flex items-center gap-2 bg-white text-black px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap flex-shrink-0 hover:bg-[#EE6A4D] hover:text-black transition-colors" data-testid="nav-cta-visit-demo">
            Visit demo <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(238,106,77,0.18),transparent_50%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-16 sm:pb-24 relative">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7 min-w-0">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#EE6A4D] mb-6">
                <Sparkles className="h-3.5 w-3.5" /> White-label SaaS · India
              </div>
              <h1 className="font-bold tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-7xl break-words">
                Run an Urban-Company-<br />
                <span className="text-[#EE6A4D]">grade</span> services app.<br />
                <span className="text-white/40">Yours. Branded.</span>
              </h1>
              <p className="mt-8 text-lg text-white/65 max-w-xl leading-relaxed">
                A multi-tenant booking platform built for India's local businesses — services, appointments, enquiries — installable as a PWA, with white-label theming, sellers, staff & subscriptions.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/t/services" className="inline-flex items-center gap-2 bg-[#EE6A4D] text-black px-6 py-3.5 rounded-full font-semibold hover:translate-y-[-2px] transition-transform" data-testid="hero-cta-customer-app">
                  Try the customer PWA <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/t/services/admin/login" className="inline-flex items-center gap-2 border border-white/15 px-6 py-3.5 rounded-full font-semibold hover:bg-white/5 transition" data-testid="hero-cta-tenant-admin">
                  Open tenant admin
                </Link>
                <Link to="/super/login" className="inline-flex items-center gap-2 border border-white/15 px-6 py-3.5 rounded-full font-semibold hover:bg-white/5 transition" data-testid="hero-cta-super">
                  Super admin
                </Link>
              </div>
              <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-md">
                {stats.map((s) => (
                  <div key={s.label}>
                    <div className="text-3xl font-bold tracking-tight">{s.value}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Phone preview */}
            <div className="md:col-span-5 relative">
              <div className="relative aspect-[9/19] w-full max-w-[300px] mx-auto rounded-[36px] border-[10px] border-[#1A1A1A] bg-[#FFF8F3] shadow-[0_30px_80px_rgba(238,106,77,0.25)] overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-6 bg-[#1A1A1A] rounded-b-2xl mx-auto w-32" />
                <div className="pt-10 px-4 text-[#1C1410]">
                  <div className="flex items-center justify-between text-[10px] font-semibold opacity-70">
                    <span>9:41</span>
                    <span>•••</span>
                  </div>
                  <div className="mt-5">
                    <div className="text-[11px] opacity-50">Welcome to</div>
                    <div className="text-xl font-bold tracking-tight">LocalAppStore Services</div>
                  </div>
                  <div className="mt-4 bg-[#EE6A4D] rounded-2xl p-4 text-white">
                    <div className="text-xs opacity-80">Best of summer</div>
                    <div className="text-lg font-bold leading-tight mt-1">Salon at home, only ₹249</div>
                  </div>
                  <div className="mt-4 grid grid-cols-4 gap-3 text-center">
                    {["Clean","AC","Salon","Repair"].map((c) => (
                      <div key={c}>
                        <div className="aspect-square rounded-2xl bg-[#FCE6DD]" />
                        <div className="text-[10px] mt-1.5 font-semibold">{c}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 text-xs font-bold uppercase tracking-wider opacity-50">Popular</div>
                  <div className="mt-2 space-y-2">
                    {["Deep Cleaning","AC Service"].map((s, i) => (
                      <div key={s} className="bg-white rounded-2xl p-3 flex items-center gap-3 shadow-sm">
                        <div className="h-12 w-12 rounded-xl bg-[#FCE6DD]" />
                        <div className="flex-1">
                          <div className="text-sm font-bold">{s}</div>
                          <div className="text-[11px] opacity-60">₹{i ? 449 : 1299} · 60 min</div>
                        </div>
                        <div className="text-xs font-semibold text-[#EE6A4D]">Book</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-white/5 bg-[#0E0E0F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#EE6A4D] mb-3">What's inside</div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-[1.05]">Everything a tenant needs.<br /><span className="text-white/40">Nothing they don't.</span></h2>
            </div>
            <Link to="/t/services" className="text-sm text-white/60 hover:text-white inline-flex items-center gap-1.5">
              See it live <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((f) => (
              <div key={f.title} className="border border-white/8 rounded-2xl p-6 hover:border-[#EE6A4D]/40 hover:bg-[#EE6A4D]/[0.03] transition-colors group">
                <f.icon className="h-6 w-6 text-[#EE6A4D] mb-5" />
                <div className="font-semibold text-lg tracking-tight">{f.title}</div>
                <div className="text-sm text-white/55 mt-2 leading-relaxed">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Themes */}
      <section id="themes" className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="mb-10">
            <div className="text-xs uppercase tracking-[0.25em] text-[#EE6A4D] mb-3">Theme presets</div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">Six aesthetics. One click.</h2>
            <p className="text-white/55 max-w-2xl mt-3">Every tenant picks a curated look — colors, type pairing, radius. They're free to override later.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {THEME_LIST.map((t) => (
              <div key={t.id} className="rounded-2xl border border-white/10 p-5 hover:border-white/30 transition" data-testid={`theme-card-${t.id}`}>
                <div className="flex items-center gap-2 mb-4">
                  {t.swatch.map((c, i) => (
                    <div key={i} className="h-6 w-6 rounded-full border border-white/10" style={{ background: c }} />
                  ))}
                </div>
                <div className="font-bold tracking-tight text-lg">{t.name}</div>
                <div className="text-sm text-white/55 mt-1 leading-relaxed">{t.vibe}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/30 mt-4">{t.mode} mode</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo CTA */}
      <section id="demo" className="border-t border-white/5 bg-gradient-to-br from-[#EE6A4D]/15 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
          <Building2 className="h-10 w-10 text-[#EE6A4D] mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold tracking-tight">Try the demo tenant.</h2>
          <p className="text-white/60 mt-4 text-lg break-words">services.localappstore.in — packed with 10 categories & 20+ services.</p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link to="/t/services" className="inline-flex items-center gap-2 bg-white text-black px-6 py-3.5 rounded-full font-semibold hover:bg-[#EE6A4D] transition" data-testid="footer-cta-customer">
              Customer PWA <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/t/services/admin/login" className="inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 rounded-full font-semibold hover:bg-white/5 transition">
              Tenant Admin
            </Link>
            <Link to="/super/login" className="inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 rounded-full font-semibold hover:bg-white/5 transition">
              Super Admin
            </Link>
          </div>
          {/* Development test accounts: shown in development builds only, never compiled into production.
              They work only while the backend runs in test mode (APP_ENV=development, OTP_TEST_MODE=true). */}
          {process.env.NODE_ENV !== "production" && (
            <div className="text-xs text-white/40 mt-8 leading-relaxed max-w-lg mx-auto break-anywhere">
              <div>Sign-in is by mobile number and OTP. Test accounts (test mode only):</div>
              <div>Super Admin: <span className="font-mono text-white/70">9858558555</span> / OTP <span className="font-mono text-white/70">557725</span></div>
              <div>Tenant admin: <span className="font-mono text-white/70">9797979797</span> · Customer: <span className="font-mono text-white/70">9898989898</span> / OTP <span className="font-mono text-white/70">123456</span></div>
            </div>
          )}
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 text-center text-xs text-white/40">
        © LocalAppStore — Multi-tenant Services Booking SaaS · India
      </footer>
    </div>
  );
}
